import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Minus, Plus, Trash2, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, total, increaseQty, decreaseQty, removeItem } = useCart();

  return (
    <div className="min-h-screen bg-[#FFF7FB] px-6 pt-10 pb-40">
      {/* HEADER - Premium Boutique Style */}
      <div className="mb-10 text-center">
        <p className="text-[10px] tracking-[0.5em] uppercase text-[#C06283] font-black opacity-60 mb-2">
          Ane Crochet
        </p>
        <h2 className="text-3xl font-serif italic text-[#5B2E3C]">
          Your Collection <span className="not-italic text-sm opacity-30">({cart.length})</span>
        </h2>
      </div>

      {/* CART ITEMS */}
      <div className="max-w-md mx-auto">
        <AnimatePresence mode="popLayout">
          {cart.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, x: -20 }}
              transition={{ duration: 0.4, ease: "circOut" }}
              className="group relative bg-white border border-pink-50 rounded-[2.5rem] p-6 mb-6 shadow-[0_15px_35px_rgba(255,230,240,0.5)] hover:shadow-[0_20px_45px_rgba(255,190,215,0.4)] transition-all"
            >
              <div className="flex items-center gap-6">
                {/* ARTISTIC MONOGRAM (REPLACING IMAGE) */}
                <div className="relative w-16 h-20 rounded-2xl bg-gradient-to-tr from-[#FFE0EE] via-[#FFD3E8] to-[#FFF0F6] flex-shrink-0 flex items-center justify-center shadow-inner">
                  <span className="text-3xl font-serif italic font-black text-white/90 select-none">
                    {item.name.charAt(0)}
                  </span>
                  <div className="absolute inset-0 bg-white/10 mix-blend-overlay pointer-events-none" />
                </div>

                {/* DETAILS */}
                <div className="flex-1 space-y-3">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-base text-[#4A1F2A] leading-tight pr-6">
                      {item.name}
                    </h3>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="absolute top-6 right-6 text-gray-200 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-lg font-serif italic text-[#FF6D95]">
                      ₹{item.price.toLocaleString()}
                    </p>

                    {/* QTY CONTROLS - Floating Pill Design */}
                    <div className="flex items-center bg-[#FFF7FB] rounded-full p-1 border border-pink-50 shadow-sm">
                      <button
                        onClick={() => decreaseQty(item.id)}
                        className="w-8 h-8 rounded-full bg-white text-[#C06283] flex items-center justify-center hover:bg-pink-50 transition-colors shadow-sm"
                      >
                        <Minus size={12} />
                      </button>

                      <span className="w-8 text-center font-black text-xs text-[#6B3442]">
                        {item.qty}
                      </span>

                      <button
                        onClick={() => increaseQty(item.id)}
                        className="w-8 h-8 rounded-full bg-[#5B2E3C] text-white flex items-center justify-center hover:bg-[#4A2631] transition-all shadow-md active:scale-90"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* EMPTY STATE */}
      {cart.length === 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-20 space-y-4"
        >
          <div className="text-5xl">🧶</div>
          <p className="font-serif italic text-gray-400">Your basket is light as air...</p>
          <button 
            onClick={() => navigate("/")}
            className="text-xs font-bold uppercase tracking-widest text-pink-400 border-b border-pink-200 pb-1"
          >
            Go Shop
          </button>
        </motion.div>
      )}

      {/* FOOTER - The Grand Finale */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-pink-50 rounded-t-[3rem] px-8 pt-8 pb-10 shadow-[0_-20px_50px_rgba(0,0,0,0.05)]">
          <div className="max-w-md mx-auto">
            <div className="flex justify-between items-end mb-8 px-2">
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-[0.4em] text-[#B36885] font-black opacity-50">
                  Total Investment
                </span>
                <p className="text-4xl font-serif text-[#6B3442] leading-none">
                  ₹{total?.toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[9px] text-pink-400 font-bold uppercase tracking-widest italic leading-none mb-1">
                  Secure Checkout
                </p>
                <div className="h-[2px] w-8 bg-pink-100 ml-auto"></div>
              </div>
            </div>

            <button
              onClick={() => navigate("/address")}
              className="w-full h-16 rounded-[2rem] bg-[#5B2E3C] text-white font-bold text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-3 shadow-[0_20px_40px_rgba(91,46,60,0.3)] hover:bg-[#4A2631] transition-all active:scale-95 group"
            >
              Continue to Shipping
              <ChevronRight size={20} className="group-hover:translate-x-2 transition-transform duration-300" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 